import { useCallback, useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { Modal, Select, useToast } from '../../../admin/primitives.js'
import { Field, TextInput } from '../../../admin/form.js'
import { useConfirm } from '../../../shell/confirm.js'
import { Link } from '../../../router.js'
import { fmtDateHeure, fmtHeure, lireSupervision, type Lecture } from '../../api.js'
import { BandeauLecture, fmtNombre, nomSalle } from '../commun.js'
import { ecrireSonde } from './ecrire.js'
import { LigneRetour, Vide, type Retour } from './elements.js'
import {
  SORTES_REGISTRE, acteur, estListePropositions, expliquerTemoin, libelleType, preuvesLisibles,
  type ListePropositions, type Proposition,
} from './modele.js'

/**
 * ONGLET « PROPOSITIONS » (21/10/2026) — ce que Sonde a appris et PROPOSE ;
 * un humain décide. Domaine d'alimentation appris (accepter = confirmer),
 * fiche fantôme (accepter = retirer le poste), exemplaire remplacé.
 * `accept_effect_fr` dit l'effet avant le geste ; refuser demande un motif et
 * suspend la proposition (Sonde dit jusqu'à quand). Le signalement Parc
 * proposé vit dans le Centre d'activité, pas ici.
 */

const STATUTS = [
  { value: 'proposed', label: 'À décider' },
  { value: 'accepted', label: 'Acceptées' },
  { value: 'dismissed', label: 'Refusées' },
  { value: 'expired', label: 'Expirées' },
]
const LIBELLE_STATUT: Record<string, string> = { proposed: 'À décider', accepted: 'Acceptée', dismissed: 'Refusée', expired: 'Expirée' }

export function OngletPropositions(props: { peutGerer: boolean; onCompte: () => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [statut, setStatut] = useState('proposed')
  const [lecture, setLecture] = useState<Lecture<ListePropositions> | null>(null)
  const [items, setItems] = useState<Proposition[]>([])
  const [curseur, setCurseur] = useState<string | null>(null)
  const [luA, setLuA] = useState<string | null>(null)
  const [occupe, setOccupe] = useState<string | null>(null)
  const [retour, setRetour] = useState<Retour>(null)
  const [refus, setRefus] = useState<Proposition | null>(null)

  const charger = useCallback(async (suite: string | null = null) => {
    const l = await lireSupervision(
      `/v/proposals?status=${statut}&kind=${SORTES_REGISTRE.join(',')}&limit=200${suite ? `&cursor=${encodeURIComponent(suite)}` : ''}`,
      estListePropositions,
    )
    setLecture(l)
    if (l.etat !== 'ok') return
    setLuA(new Date().toISOString())
    setCurseur(l.data.next_cursor)
    setItems((avant) => (suite ? [...avant, ...l.data.items.filter((p) => !avant.some((a) => a.id === p.id))] : l.data.items))
  }, [statut])

  useEffect(() => { void charger() }, [charger])

  const accepter = async (p: Proposition) => {
    const ok = await confirm({
      title: `${p.kind_label} : accepter ?`, message: p.accept_effect_fr || 'Sonde applique la proposition.',
      confirmLabel: p.kind === 'retire_ghost' ? 'Retirer le poste' : 'Accepter',
      variant: p.kind === 'retire_ghost' ? 'critical' : 'standard',
      ...(p.device ? { details: [{ label: 'Poste', value: `${p.device.display_name} (${p.device.uid})` }] } : {}),
    })
    if (!ok) return
    setOccupe(p.id); setRetour(null)
    const e = await ecrireSonde('POST', `proposals/${encodeURIComponent(p.id)}/accept`, {})
    setOccupe(null)
    if (!e.ok) { setRetour({ ton: 'ko', texte: e.code === 'DEVICE_IS_POWER_ROOT' ? expliquerTemoin(e.message) : e.message }); return }
    const effet = (e.data as { effect_fr?: unknown } | null)?.effect_fr
    toast('ok', typeof effet === 'string' && effet ? effet : 'Proposition acceptée.')
    void charger(); props.onCompte()
  }

  const counts = lecture?.etat === 'ok' ? lecture.data.counts : null

  return (
    <section className="sv-rgs-onglet" aria-label="Propositions de Sonde">
      <div className="sv-rgs-outils">
        <Select compact ariaLabel="Statut des propositions" value={statut} options={STATUTS} onChange={setStatut} />
        <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { void charger(); props.onCompte() }}>
          <RefreshCw size={14} aria-hidden="true" /> Relire
        </button>
      </div>
      <p className="sv-rgs-source">
        Source : Sonde, propositions du registre{luA ? ` · lues à ${fmtHeure(luA)}` : ''}
        {counts ? ` · toutes sortes confondues : ${fmtNombre(counts.proposed)} à décider, ${fmtNombre(counts.accepted)} acceptées, ${fmtNombre(counts.dismissed)} refusées` : ''}
      </p>
      <BandeauLecture lecture={lecture} depuis={luA} />
      <LigneRetour retour={retour} />

      {lecture?.etat === 'ok' && items.length === 0 ? (
        statut === 'proposed' ? (
          <Vide titre="Aucune proposition à décider.">
            Sonde propose ici ce qu’elle apprend : un domaine d’alimentation (des appareils qui s’éteignent ensemble), une fiche fantôme
            (un poste jamais revu), un exemplaire remplacé. Elle a besoin de quelques soirées d’observation et d’appareils validés au
            registre pour apprendre. Dernière lecture : {fmtHeure(luA)}.
          </Vide>
        ) : <Vide titre="Aucune proposition dans cet état." />
      ) : null}

      <ul className="sv-rgs-propositions">
        {items.map((p) => (
          <li key={p.id} className="lc-panel sv-rgs-proposition">
            <div className="lc-panel__body">
              <div className="sv-rgs-ligne__titre">
                <span className="sv-rgs-nom">{p.kind_label}</span>
                <span className={`sv-rgs-geste sv-rgs-geste--${p.status === 'proposed' ? 'attach' : 'fait'}`}>{LIBELLE_STATUT[p.status] ?? p.status}</span>
              </div>
              <p className="sv-rgs-raison">{p.reason}</p>
              <p className="sv-rgs-meta">
                {p.device ? (
                  <span>
                    <Link href={`/infrastructure/appareils/${encodeURIComponent(p.device.uid)}`}>{p.device.display_name}</Link>
                    {' '}· {libelleType(p.device.kind)}{p.device.zone_code ? ` · ${nomSalle(p.device.zone_code)}` : ''}
                  </span>
                ) : null}
                <span>Proposée le {fmtDateHeure(p.created_at)}</span>
                {p.decided_at ? <span>{LIBELLE_STATUT[p.status]} par {acteur(p.decided_by)} le {fmtDateHeure(p.decided_at)}</span> : null}
                {p.dismiss_reason ? <span>Motif : {p.dismiss_reason}</span> : null}
                {p.reproposal_after ? <span>Pas de nouvelle proposition avant le {fmtDateHeure(p.reproposal_after)}</span> : null}
              </p>
              {Object.keys(p.evidence).length > 0 ? (
                <dl className="sv-rgs-preuves">
                  {preuvesLisibles(p.evidence).map((x) => <div key={x.cle}><dt>{x.libelle}</dt><dd>{x.valeur}</dd></div>)}
                </dl>
              ) : null}
              {p.status === 'proposed' ? <p className="sv-rgs-effet"><strong>Accepter :</strong> {p.accept_effect_fr || '—'}</p> : null}
              {props.peutGerer && p.status === 'proposed' ? (
                <div className="sv-rgs-ligne__actions">
                  <button type="button" className={`lc-btn lc-btn--sm${p.kind === 'retire_ghost' ? ' lc-btn--danger' : ' lc-btn--primary'}`}
                    disabled={occupe !== null} onClick={() => void accepter(p)}>
                    {p.kind === 'retire_ghost' ? 'Retirer le poste' : p.kind === 'power_domain' ? 'Confirmer le domaine' : 'Accepter'}
                  </button>
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" disabled={occupe !== null} onClick={() => setRefus(p)}>Refuser</button>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {curseur ? (
        <p className="sv-rgs-suite"><button type="button" className="lc-btn" onClick={() => void charger(curseur)}>Charger les suivantes</button></p>
      ) : null}

      {refus ? (
        <ModaleRefus proposition={refus} onFermer={() => setRefus(null)}
          onFait={(texte) => { setRefus(null); toast('ok', texte); void charger(); props.onCompte() }} />
      ) : null}
    </section>
  )
}

function ModaleRefus(props: { proposition: Proposition; onFermer: () => void; onFait: (texte: string) => void }) {
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [retour, setRetour] = useState<Retour>(null)
  const p = props.proposition
  const envoyer = async () => {
    setEnvoi(true)
    const e = await ecrireSonde('POST', `proposals/${encodeURIComponent(p.id)}/dismiss`, { reason: motif.trim() })
    setEnvoi(false)
    if (!e.ok) { setRetour({ ton: 'ko', texte: e.message }); return }
    const effet = (e.data as { effect_fr?: unknown } | null)?.effect_fr
    props.onFait(typeof effet === 'string' && effet ? effet : 'Proposition refusée.')
  }
  return (
    <Modal open onClose={() => { if (!envoi) props.onFermer() }} ariaLabel="Refuser la proposition" width={560} owning>
      <div className="lc-modal__head"><h2 className="lc-modal__title">Refuser : {p.kind_label}</h2></div>
      <div className="lc-modal__body sv-rgs-modale">
        <p className="sv-rgs-aide">{p.reason}</p>
        <Field label="Motif (obligatoire)" helper="Noté dans le journal de Sonde. Elle ne reproposera rien pour cet appareil pendant quelques jours.">
          <TextInput value={motif} onChange={setMotif} maxLength={500} autoFocus ariaLabel="Motif du refus" />
        </Field>
        <LigneRetour retour={retour} />
        <div className="sv-rgs-pied">
          <button type="button" className="lc-btn lc-btn--ghost" disabled={envoi} onClick={props.onFermer}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={envoi || !motif.trim()} onClick={() => void envoyer()}>
            {envoi ? 'Envoi…' : 'Refuser'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
